/// <reference path="../invoices/InvoiceLineCell.ts" />
/// <reference path="../purchase_invoices/PurchaseInvoiceLineCell.ts" />
/// <reference path="DocumentExtractionLineCell.ts" />

// A line detected on a purchase invoice, shown as the invoice's own add-line row pre-filled
// from the extraction. The text printed on the invoice is the label above the product field;
// the field holds the product or concept the server matched, or is empty to search for one
// (typing opens the editor's picker). The icon next to the label offers the other choices:
// create a product, keep the line as a concept line. Accepting the row (+)
// creates the line exactly as typing it in the editor would.

class InvoiceExtractedLineCell extends PurchaseInvoiceLineCell
{
    session:DocumentExtractionSession = null;

    private optionsButton:MUIButton = null;
    private acceptButton:MUIButton = null;
    private selectButton:MUICheckButton = null;

    awakeFromHTML(){
        super.awakeFromHTML();
        this.selectButton = MUIOutlet(this, "select-cb", "MUICheckButton");
        if (this.selectButton) this.selectButton.setOnChangeValue(this, function(this:InvoiceExtractedLineCell, control:MUICheckButton, value:boolean){
            if (this.session != null && this._row != null) this.session.toggleSelection(this._row, value);
        });
        this.optionsButton = MUIOutlet(this, "options-btn", "MUIButton");
        if (this.optionsButton) this.optionsButton.setAction(this, function(this:InvoiceExtractedLineCell){ this.showOptions(); });
        this.acceptButton = MUIOutlet(this, "accept-btn", "MUIButton");
        if (this.acceptButton) this.acceptButton.setAction(this, function(this:InvoiceExtractedLineCell){ this.acceptDidPress(); });
    }

    private _row:DocumentExtractionReviewRow = null;
    get proposalRow() : DocumentExtractionReviewRow { return this._row; }

    set proposalRow(row:DocumentExtractionReviewRow){
        this.reset();
        this._row = row;
        this.line = null;
        this.strictVat = true;     // a detected line without VAT is flagged like a line of the invoice
        this.underReview = true;   // and always shows the link field with the printed text under it
        let line = row.line ?? {};

        // the concept column: the printed text as the label, the match (if any) in the field
        this.product = row.notAProduct ? null : row.product;
        this.conceptItem = row.notAProduct ? null : row.conceptItem;
        if (this.conceptLabel) this.conceptLabel.text = DocumentExtractionLineCell.escape(line["description"] ?? "");
        this.productTextField.text = this.product != null ? this.product.productNameDescription() : (this.conceptItem != null ? this.conceptItem.name : (row.customName ?? ""));
        if (this.selectButton) {
            this.selectButton.hidden = row.group != null;    // a custom line is not grouped again
            this.selectButton.on = row.selected == true;
        }

        // VAT: the proposal's tax, the product's purchase tax, or the venue tax with the rate read
        let productTax = this.product != null ? DBHelper.stockTaxFromProduct(this.product) : null;
        this.vat = row.tax ?? productTax ?? (this.session != null ? this.session.taxForRate(line["taxRate"]) : null);
        this.setDropdownTitle(this.legalTaxDropDown, null, MIOLocalizeString("NONE", "NONE"));
        this.setDropdownTitle(this.classificationDropdown, null, MIOLocalizeString("CHOOSE", "CHOOSE"));

        // the amounts as printed: quantity x price, the discount, the base
        let quantity = Number(line["quantity"] ?? 1) || 1;
        let price = Number(line["unitPrice"] ?? 0) || 0;
        let gross = quantity * price;
        let discount = line["discount"] ?? null;
        let discountFormat:string = null;
        let discountAmount = 0;
        if (discount != null && discount["percent"] != null) { discountFormat = discount["percent"] + "%"; discountAmount = gross * Number(discount["percent"]) / 100; }
        else if (discount != null && discount["amount"] != null) { discountFormat = String(discount["amount"]); discountAmount = Number(discount["amount"]) || 0; }
        let printedBase = line["base"] != null ? Number(line["base"]) : (line["total"] != null ? Number(line["total"]) : null);
        let base = printedBase != null && !isNaN(printedBase) ? printedBase : gross - discountAmount;
        if (price == 0 && quantity > 0 && base != 0) price = base / quantity;

        this.quantity = quantity;
        this.price_amount = price;
        this.discount_format = discountFormat;
        this.discount_amount = discountAmount;
        this.base_amount = base;
        this.recalculateTaxes();

        if (this.quantityTextField) this.quantityTextField.text = this.nf.stringFromNumber(quantity);
        if (this.unitPriceTextField) this.unitPriceTextField.text = this.cf.stringFromNumber(price);
        if (this.discountTextField) this.discountTextField.text = discountFormat;
        if (this.totalTextField) this.totalTextField.text = this.cf.stringFromNumber(base);

        this.updateLinkUI();
    }

    private get printedText() : string { return this._row != null && this._row.line != null ? (this._row.line["description"] ?? "") : ""; }

    // The picker's choice (typing in the field): only the link changes, the printed amounts
    // stay (the editor's version resets the row).
    protected itemDidSelect(controller:SelectEntityViewController, item:CoreItemList){
        if (item == null) return;
        if (item.product != null) this.linkProduct(item.product);
        else if (item.conceptItem != null) {
            this.product = null;
            this.conceptItem = item.conceptItem;
            this.productTextField.text = item.conceptItem.name;
            this.updateLinkUI();
        }
    }

    private linkProduct(product:Product){
        let vat = this.vat;
        this.conceptItem = null;
        this.productDidSelect(null, product, null);       // sets the product and its purchase tax
        if (this.vat == null) this.vat = vat;
        this.recalculateTaxes();
        this.updateLinkUI();
    }

    // The choices for a line that is not a product of the venue yet: create a product from the
    // printed text, or book it as a concept line (searching is what the field itself does).
    private showOptions(){
        if (this._row != null && this._row.group != null) { this.showCustomLineOptions(); return; }
        let avc = new MUIAlertViewController();
        avc.initWithTitle(MIOLocalizeString("DETECTED_LINE", "DETECTED LINE"), DocumentExtractionLineCell.escape(this.printedText), MUIAlertViewStyle.Default);
        avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("NEW_PRODUCT", "NEW PRODUCT"), MUIAlertActionStyle.Default, this, function(this:InvoiceExtractedLineCell){
            AppHelper.sharedInstance().showAddStockProductAlertViewController(this.printedText, null, (product:Product) => {
                if (product != null) this.linkProduct(product);
            });
        }));
        avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("KEEP_AS_CONCEPT_LINE", "KEEP AS CONCEPT LINE"), MUIAlertActionStyle.Default, this, function(this:InvoiceExtractedLineCell){
            this.product = null;
            this.conceptItem = null;
            this.productTextField.text = this.printedText;
            this.recalculateTaxes();
            this.updateLinkUI();
            this.enterDidPress();
        }));
        // this row, with the rows picked with their checkbox, as one concept line
        let others = this.session != null ? this.session.selectedCount - (this._row != null && this._row.selected ? 1 : 0) : 0;
        let title = others > 0 ? MIOLocalizeString("CUSTOM_LINE_WITH_THE_SELECTED_LINES", "CUSTOM LINE WITH THE SELECTED LINES") + " (" + (others + 1) + ")"
                               : MIOLocalizeString("CUSTOM_LINE_WITH_THIS_LINE", "CUSTOM LINE WITH THIS LINE");
        avc.addAction(MUIAlertAction.alertActionWithTitle(title, MUIAlertActionStyle.Default, this, function(this:InvoiceExtractedLineCell){
            if (this.session != null && this._row != null) this.session.createCustomLine(this._row);
        }));
        avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("CANCEL", "CANCEL"), MUIAlertActionStyle.Cancel, null, null));
        let host = this.session != null ? this.session.host : null;
        if (host != null) host.presentViewController(avc, true);
    }

    // A custom line stands for detected rows: the only choice is to split them again.
    private showCustomLineOptions(){
        let row = this._row;
        let avc = new MUIAlertViewController();
        avc.initWithTitle(MIOLocalizeString("CUSTOM_LINE", "CUSTOM LINE"), DocumentExtractionLineCell.escape(this.printedText), MUIAlertViewStyle.Default);
        avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("UNGROUP_THE_LINES", "UNGROUP THE LINES"), MUIAlertActionStyle.Default, this, function(this:InvoiceExtractedLineCell){
            if (this.session != null) this.session.ungroup(row);
        }));
        avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("CANCEL", "CANCEL"), MUIAlertActionStyle.Cancel, null, null));
        let host = this.session != null ? this.session.host : null;
        if (host != null) host.presentViewController(avc, true);
    }

    // + on a row: accept it; on a row with an empty field the choices are offered instead.
    private acceptDidPress(){
        if ((this.productTextField.text ?? "").length == 0) { this.showOptions(); return; }
        this.enterDidPress();
    }

    // The editor's own accept path creates the line from the fields (a concept item is created
    // for a text that is neither a product nor a known concept) and tells the delegate; the
    // session is the delegate and needs to know which detected row it was.
    enterDidPress(){
        if (this.session != null) this.session.insertingRow = this._row;
        super.enterDidPress();
        if (this.session != null) this.session.insertingRow = null;
    }
}
